import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { EmptyState } from "@sylviq/ui-react-web/components/ui/empty-state";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  ExtensionViewBlock,
  parseExtensionViewSurface,
  type ExtensionViewCommand,
  type ExtensionViewSurface,
} from "@sylviq/ui-react-web/blocks/extension";
import { useEffect, useState } from "react";
import { invokeExtensionV2, type ExtensionV2Response } from "@/lib/api";
import { useOrganization } from "@/lib/organization";
import { scopeExtensionViewSurface } from "./scoped-links";

type ViewState =
  | { status: "loading" }
  | { status: "loaded"; surface: ExtensionViewSurface; renderToken: string }
  | { status: "error"; message: string };

export function ExtensionV2ViewPage({
  installationId,
  viewId,
}: {
  installationId: string;
  viewId: string;
}) {
  const { scope } = useOrganization();
  const [state, setState] = useState<ViewState>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<{ tone: "saved" | "error"; text: string } | null>(null);

  useEffect(() => {
    if (!scope?.organization_id) {
      setState({ status: "error", message: "The organization context is unavailable." });
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });
    void (async () => {
      try {
        const response = await invokeExtensionV2(
          scope.organization_id,
          installationId,
          { kind: "view", app: "niche-accounting", payload: { viewId } },
        );
        if (cancelled) return;
        if (!response.ok) {
          setState({
            status: "error",
            message: response.error?.message ?? "That extension view is not available.",
          });
          return;
        }
        const surface = parseExtensionViewSurface(response.result);
        if (!surface) {
          setState({ status: "error", message: "The extension returned an invalid view." });
          return;
        }
        setState({ status: "loaded", surface, renderToken: crypto.randomUUID() });
      } catch {
        if (!cancelled) {
          setState({ status: "error", message: "The extension view could not be loaded." });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [attempt, installationId, scope?.organization_id, viewId]);

  function submit(invocation: ExtensionViewCommand) {
    if (state.status !== "loaded" || saving || !scope?.organization_id) return;
    const values: Record<string, string> = {};
    for (const [name, value] of Object.entries(invocation.values ?? {})) {
      if (typeof value !== "string" || !value) continue;
      if (!/^[A-Za-z][A-Za-z0-9._-]{0,80}$/.test(name)) {
        setNotice({ tone: "error", text: "The extension command is invalid." });
        return;
      }
      if (value.includes("\u0000") || new TextEncoder().encode(value).length > 8192) {
        setNotice({ tone: "error", text: "The extension command is invalid." });
        return;
      }
      values[name] = value;
    }
    if (Object.keys(values).length === 0) {
      setNotice({ tone: "error", text: "Enter the values before submitting." });
      return;
    }
    setSaving(true);
    setNotice(null);
    void (async () => {
      try {
        const response: ExtensionV2Response = await invokeExtensionV2(
          scope.organization_id,
          installationId,
          {
            kind: "ui_command",
            app: "niche-accounting",
            payload: {
              command: invocation.command,
              values,
              viewId,
              selectedIds: [],
              renderToken: state.renderToken,
            },
          },
        );
        if (!response.ok) {
          throw new Error(response.error?.message ?? "extension command failed");
        }
        setNotice({ tone: "saved", text: "The extension accepted the command." });
        setAttempt((current) => current + 1);
      } catch {
        setNotice({ tone: "error", text: "The extension command failed." });
      } finally {
        setSaving(false);
      }
    })();
  }

  if (state.status === "loading") {
    return (
      <div className="grid gap-4">
        <PageHeader title="Extension" description="Loading the installed extension…" />
        <p role="status" className="text-sm text-[var(--muted-foreground)]">Loading…</p>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="grid gap-4">
        <PageHeader title="Extension" description="The installed extension view could not be opened." />
        <EmptyState
          title="Extension view unavailable"
          reason={state.message}
          action={<Button type="button" variant="secondary" onClick={() => setAttempt((value) => value + 1)}>Retry</Button>}
        />
      </div>
    );
  }

  return (
    <div className="grid gap-4">
      <ExtensionViewBlock
        surface={scope ? scopeExtensionViewSurface(state.surface, scope) : state.surface}
        onCommand={submit}
      />
      {notice ? (
        <p
          role={notice.tone === "error" ? "alert" : "status"}
          className={`text-sm ${notice.tone === "error" ? "text-[var(--danger)]" : "text-[var(--muted-foreground)]"}`}
        >
          {notice.text}
        </p>
      ) : null}
    </div>
  );
}
